import { defineStore } from 'pinia';
import {
	Firestore,
	collection,
	doc,
	getDocs,
	setDoc,
	deleteDoc,
	DocumentSnapshot,
	QueryDocumentSnapshot,
	type DocumentData,
} from 'firebase/firestore';
import { type HomeWorld, getDefaultHomeWorld, homeWorld2FeatureOption } from '../models/homeworld';
import { type FeatureOptions } from '~/models/feature';
import { generatePropertyId } from '~/utils/idGenerator';

export interface HomeWorldStateInterface {
	homeWorlds: HomeWorld[];
	loaded: boolean;
	loading: boolean;
}

export const useHomeWorldStore = defineStore('HomeWorldStore', {
	state: (): HomeWorldStateInterface => ({
		homeWorlds: [],
		loaded: false,
		loading: false,
	}),

	getters: {
		/**
		 * Get homeworld by ID
		 */
		homeWorldById: (state) => {
			return (homeWorldId: string): HomeWorld | undefined =>
				state.homeWorlds.find((hw) => hw.id === homeWorldId);
		},

		/**
		 * Get homeworlds as FeatureOptions for editor
		 */
		getOptions: (state): FeatureOptions => {
			return state.homeWorlds.map((hw) => homeWorld2FeatureOption(hw));
		},

		/**
		 * Get sorted homeworlds by name
		 */
		sortedHomeWorlds: (state): HomeWorld[] => {
			return [...state.homeWorlds].sort((a, b) => a.name.localeCompare(b.name));
		},
	},

	actions: {
		/**
		 * Fetch all homeworlds from Firestore properties collection
		 * Note: Homeworlds are now stored as properties with type='homeWorld'
		 */
		async fetchAll() {
			console.log('Fetching all homeworlds from properties collection');
			if (!this.loaded && !this.loading) {
				this.loading = true;
				const { $firestore } = useNuxtApp();
				try {
					// Fetch homeworlds from properties collection by type
					const propertyStore = usePropertyStore();
					await propertyStore.fetchAll();

					// Filter properties for homeWorld type and convert to HomeWorld objects
					const homeworldProperties = propertyStore.propertiesByType('homeWorld');
					const items = homeworldProperties.map((prop) => {
						// Convert property to HomeWorld object
						return {
							id: prop.id,
							name: prop.name,
							tag: prop.tag,
							description: prop.description,
							imageURL: prop.imageURL,
							docURL: prop.docURL,
							originId: undefined,
							propertyRefs: prop.effectRefs || [], // effectRefs on property become propertyRefs on homeworld
							properties: [],
							createdAt: prop.createdAt,
							updatedAt: prop.updatedAt,
						} as HomeWorld;
					});

					for (const homeWorld of items) {
						if (homeWorld) {
							await this.loadProperties(homeWorld);
							this.addHomeWorld(homeWorld);
						}
					}

					this.loaded = true;
					console.log(`Loaded ${items.length} homeworlds from properties`);
					return items;
				} catch (error) {
					console.error('Error fetching homeworlds:', error);
					throw error;
				} finally {
					this.loading = false;
				}
			}
		},

		/**
		 * Load properties for a homeworld
		 */
		async loadProperties(homeWorld: HomeWorld) {
			const propertyStore = usePropertyStore();
			homeWorld.properties = [];

			// Bundle path: source property has _effectRefIds instead of effectRefs
			const sourceProperty = propertyStore.propertyById(homeWorld.id);
			if (sourceProperty?._effectRefIds?.length) {
				for (const id of sourceProperty._effectRefIds) {
					const property = propertyStore.propertyById(id);
					if (property) homeWorld.properties.push(property);
				}
				return;
			}

			// Firestore path: use propertyRefs (DocumentReferences)
			for (const ref of homeWorld.propertyRefs) {
				const property = propertyStore.propertyById(ref.id);
				if (property) {
					homeWorld.properties.push(property);
				}
			}
		},

		/**
		 * Convert Firestore document to HomeWorld object
		 */
		getDocFromSnapshot(
			doc:
				| DocumentSnapshot<DocumentData, DocumentData>
				| QueryDocumentSnapshot<DocumentData, DocumentData>
		): HomeWorld {
			return { ...getDefaultHomeWorld(), id: doc.id, ...doc.data() } as HomeWorld;
		},

		/**
		 * Add homeworld to store
		 */
		addHomeWorld(homeWorld: HomeWorld) {
			const existingIndex = this.homeWorlds.findIndex((hw) => hw.id === homeWorld.id);
			if (existingIndex > -1) {
				this.homeWorlds[existingIndex] = homeWorld;
			} else {
				this.homeWorlds.push(homeWorld);
			}
		},

		/**
		 * Upsert homeworld to Firestore properties collection
		 * Note: Homeworlds are now stored as properties with type='homeWorld'
		 */
		async upsert(homeWorld: HomeWorld) {
			const { $firestore } = useNuxtApp();

			// Check if the homeworld already has an ID
			if (homeWorld.id === '-1' || homeWorld.id === undefined || !homeWorld.id) {
				// Generate standardized ID: homeworld-<name>
				homeWorld.id = generatePropertyId('homeWorld', homeWorld.name);
				console.log(`Generated new homeworld ID: ${homeWorld.id}`);
			}

			const homeWorldRef = doc($firestore as Firestore, `properties/${homeWorld.id}`);

			// Convert homeworld to property format
			const propertyData = {
				id: homeWorld.id, // Ensure internal ID matches document ID
				name: homeWorld.name,
				type: 'homeWorld',
				description: homeWorld.description,
				imageURL: homeWorld.imageURL,
				docURL: homeWorld.docURL,
				effectRefs: homeWorld.propertyRefs || [], // propertyRefs on homeworld become effectRefs on property
				tag: homeWorld.tag,
				sortOrder: 0,
				createdAt: homeWorld.createdAt || Date.now(),
				updatedAt: Date.now(),
			};

			await setDoc(homeWorldRef, propertyData);
			this.addHomeWorld(homeWorld);
			console.log(`HomeWorld ${homeWorld.name} saved to properties with ID: ${homeWorld.id}`);
		},

		/**
		 * Delete homeworld from Firestore properties collection
		 * Note: Homeworlds are now stored as properties with type='homeWorld'
		 */
		async delete(homeWorldId: string) {
			const { $firestore } = useNuxtApp();
			await deleteDoc(doc($firestore as Firestore, `properties/${homeWorldId}`));
			this.homeWorlds = this.homeWorlds.filter((hw) => hw.id !== homeWorldId);
			console.log(`HomeWorld ${homeWorldId} deleted from properties`);
		},

		/**
		 * Add property to homeworld
		 */
		async addProperty(homeWorld: HomeWorld, property: any) {
			const propertyStore = usePropertyStore();
			const propertyRef = propertyStore.getPropertyRef(property.id);

			if (propertyRef && !homeWorld.propertyRefs.some((ref) => ref.id === property.id)) {
				homeWorld.propertyRefs.push(propertyRef);
				homeWorld.properties.push(property);
				await this.upsert(homeWorld);
			}
		},

		/**
		 * Remove property from homeworld
		 */
		async removeProperty(homeWorld: HomeWorld, property: any) {
			homeWorld.propertyRefs = homeWorld.propertyRefs.filter((ref) => ref.id !== property.id);
			homeWorld.properties = homeWorld.properties.filter((p) => p.id !== property.id);
			await this.upsert(homeWorld);
		},

		/**
		 * Clear all homeworlds
		 */
		clear() {
			this.homeWorlds = [];
			this.loaded = false;
			this.loading = false;
		},
	},
});
